import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, X, Crown, Calendar, AlertCircle, CreditCard, CheckCircle, Loader2, Heart, Sparkles, XCircle } from 'lucide-angular';
import { AuthService } from '../../../common/auth/auth.service';
import { UserSubscription } from '../../../common/auth/auth.models';

type ModalView = 'info' | 'confirm' | 'verifying';

@Component({
  selector: 'app-subscription-info-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './subscription-info-modal.html',
  styleUrl: './subscription-info-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionInfoModalComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly subscription$$ = new BehaviorSubject<UserSubscription | null>(null);
  protected readonly subscription$ = this.subscription$$.asObservable();

  private readonly isCancelling$$ = new BehaviorSubject<boolean>(false);
  protected readonly isCancelling$ = this.isCancelling$$.asObservable();

  private readonly currentView$$ = new BehaviorSubject<ModalView>('info');
  protected readonly currentView$ = this.currentView$$.asObservable();

  // Progress percentage (0-100) for the verification progress bar
  private readonly progress$$ = new BehaviorSubject<number>(0);
  protected readonly progress$ = this.progress$$.asObservable();

  // Polling subscription
  private pollingSubscription: Subscription | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private readonly MAX_POLL_TIME_MS = 10000; // 10 seconds max
  private readonly POLL_INTERVAL_MS = 1000; // 1 second interval

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    // Reset states when modal opens/closes
    if (!value) {
      this.isCancelling$$.next(false);
      this.currentView$$.next('info');
      this.stopPolling();
      this.progress$$.next(0);
    }
  }

  @Input()
  set subscription(value: UserSubscription | undefined | null) {
    this.subscription$$.next(value ?? null);
    // If we're verifying and subscription now shows cancelAtPeriodEnd, complete verification
    if (this.currentView$$.value === 'verifying' && value?.cancelAtPeriodEnd) {
      this.onVerificationComplete();
    }
  }

  @Input()
  set isCancelling(value: boolean) {
    this.isCancelling$$.next(value);
  }

  @Input()
  set cancellationSuccess(value: boolean) {
    if (value) {
      this.startVerificationPolling();
    }
  }

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly cancelSubscription = new EventEmitter<void>();
  @Output() readonly verificationComplete = new EventEmitter<void>();

  protected readonly icons = {
    X,
    Crown,
    Calendar,
    AlertCircle,
    CreditCard,
    CheckCircle,
    Loader2,
    Heart,
    Sparkles,
    XCircle,
  };

  protected readonly features = [
    'Unlimited lesson plan generations',
    'Export to PDF, PPTX & Google Slides',
    'Premium AI-powered features',
    'Priority customer support',
  ];

  protected readonly loseFeatures = [
    'Unlimited lesson plan generations',
    'Export to PDF, PPTX & Google Slides',
    'Premium AI-powered features',
    'Priority customer support',
  ];

  ngOnDestroy(): void {
    this.stopPolling();
  }

  protected close(): void {
    // Don't allow closing while verifying
    if (this.currentView$$.value === 'verifying') {
      return;
    }
    this.closed.emit();
  }

  protected showConfirmation(): void {
    this.currentView$$.next('confirm');
  }

  protected goBackToInfo(): void {
    this.currentView$$.next('info');
  }

  protected onConfirmCancel(): void {
    this.cancelSubscription.emit();
  }

  private startVerificationPolling(): void {
    this.currentView$$.next('verifying');
    this.progress$$.next(0);
    const startTime = Date.now();

    // Start progress animation (updates every 100ms for smooth animation)
    this.startProgressAnimation(startTime);

    // Do an immediate first check
    this.checkCancellationStatus(startTime);

    // Then start interval polling
    this.pollingSubscription = interval(this.POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkCancellationStatus(startTime);
      });
  }

  /**
   * Animate progress bar with exponential easing
   * Starts fast and slows down as it approaches 95% (never reaches 100% until complete)
   */
  private startProgressAnimation(startTime: number): void {
    this.progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = this.calculateExponentialProgress(elapsed);
      this.progress$$.next(progress);
    }, 100); // Update every 100ms for smooth animation
  }

  /**
   * Calculate exponential progress that approaches but never reaches 95%
   * Uses exponential ease-out formula: 1 - e^(-t/tau)
   */
  private calculateExponentialProgress(elapsedMs: number): number {
    const tau = 3000; // 3 seconds time constant
    const maxProgress = 95;
    const progress = maxProgress * (1 - Math.exp(-elapsedMs / tau));
    return Math.min(progress, maxProgress);
  }

  private checkCancellationStatus(startTime: number): void {
    const elapsedTime = Date.now() - startTime;

    // Check if we've exceeded max poll time
    if (elapsedTime >= this.MAX_POLL_TIME_MS) {
      this.onVerificationComplete();
      return;
    }

    // Fetch fresh profile from API
    this.authService.refreshProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          // Check if subscription now has cancelAtPeriodEnd
          if (profile.subscription?.cancelAtPeriodEnd) {
            this.onVerificationComplete();
          }
        },
        error: () => {
          // On error, continue polling until timeout
          console.warn('Profile fetch failed, continuing to poll...');
        }
      });
  }

  private onVerificationComplete(): void {
    // Set progress to 100% before stopping
    this.progress$$.next(100);

    this.stopPolling();

    // Wait a moment to show 100% progress, then emit completion
    setTimeout(() => {
      this.verificationComplete.emit();
      this.currentView$$.next('info');
    }, 500);
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  protected formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  protected getStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      default:
        return 'Unknown';
    }
  }

  protected getStatusColorClasses(status: string | undefined): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'trialing':
        return 'bg-blue-100 text-blue-700';
      case 'past_due':
        return 'bg-amber-100 text-amber-700';
      case 'canceled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  protected getPlanDisplayName(plan: string | null | undefined): string {
    if (!plan) return 'Premium';
    // Capitalize first letter and format
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  }
}

